import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  projectsApi,
  type ProjectListItem,
  type ProjectBootstrapResult,
  ApiError,
} from "@/api/client";
import { AppShell } from "@/components/AppShell";

export default function Projects() {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resumingSlug, setResumingSlug] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ProjectBootstrapResult | null>(null);

  function load() {
    setLoading(true);
    projectsApi.list()
      .then((r) => setProjects(r.projects))
      .catch((err) => setError(err instanceof ApiError ? err.message : err.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function onResume(slug: string) {
    setResumingSlug(slug);
    setError(null);
    setLastResult(null);
    try {
      const result = await projectsApi.resume(slug);
      setLastResult(result);
      load();
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro");
    } finally {
      setResumingSlug(null);
    }
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto p-6">
        <Link to="/dashboard" className="text-sm underline text-app-muted hover:text-app-accent">
          ← dashboard
        </Link>
        <div className="flex justify-between items-center my-4">
          <h1 className="text-2xl font-bold">Projetos</h1>
          <div className="flex gap-2">
            <Link to="/admin/activity"
                  className="border border-app-border rounded px-4 py-2 text-sm font-medium hover:bg-app-surface-2">
              Atividade on-chain
            </Link>
            <Link to="/admin/projects/new"
                  className="bg-app-accent text-app-accent-text rounded px-4 py-2 text-sm font-medium hover:opacity-90">
              + Novo projeto
            </Link>
          </div>
        </div>

        {loading && <p className="text-sm text-app-muted">Carregando...</p>}
        {error && (
          <div className="bg-red-500/10 border border-red-500/40 rounded p-3 text-sm text-red-500 mb-3">{error}</div>
        )}

        {lastResult && lastResult.steps && lastResult.steps.length > 0 && (
          <div className="app-card p-4 mb-4">
            <h3 className="font-semibold mb-3">
              {lastResult.ok === false
                ? `❌ Deploy parou no meio (${lastResult.steps.filter(s => s.status === "success").length} de ${lastResult.steps.length} ok)`
                : `✅ Deploy concluído (${lastResult.steps.length} passos)`}
            </h3>
            <ul className="space-y-1 text-sm">
              {lastResult.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-0.5">
                    {step.status === "success" ? "✅" :
                     step.status === "skipped" ? "⏭️" : "❌"}
                  </span>
                  <div className="flex-1">
                    <span className="font-medium">{step.label}</span>
                    <span className="text-app-muted text-xs ml-2">[{step.category}]</span>
                    {step.address && (
                      <a target="_blank" rel="noreferrer"
                         className="ml-2 text-app-accent underline text-xs font-mono"
                         href={`https://sepolia.basescan.org/address/${step.address}`}>
                        {step.address.slice(0, 10)}...
                      </a>
                    )}
                    {step.txHash && (
                      <a target="_blank" rel="noreferrer"
                         className="ml-2 text-app-accent underline text-xs"
                         href={`https://sepolia.basescan.org/tx/${step.txHash}`}>
                        tx
                      </a>
                    )}
                    {step.reason && (
                      <span className="text-app-muted text-xs ml-2">({step.reason})</span>
                    )}
                    {step.error && (
                      <p className="text-red-500 text-xs mt-1 font-mono whitespace-pre-wrap">
                        {step.error}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {lastResult.error && lastResult.ok === false && (
              <div className="mt-3 text-xs text-app-muted">
                Você pode clicar em "↻ Continuar" novamente — os passos verdes serão pulados.
              </div>
            )}
          </div>
        )}

        {!loading && projects.length === 0 && (
          <div className="border-dashed border-2 border-app-border rounded-lg p-8 text-center text-app-muted">
            Nenhum projeto ainda. Clique em "Novo projeto" pra começar.
          </div>
        )}

        {projects.length > 0 && (
          <div className="app-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-app-surface-2">
                <tr className="border-b border-app-border text-left text-xs uppercase text-app-muted">
                  <th className="px-3 py-2">Slug</th>
                  <th className="py-2">Nome</th>
                  <th>Símbolo</th>
                  <th>Status</th>
                  <th>Token</th>
                  <th className="pr-3">Criado</th>
                  <th className="pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} className="border-b border-app-border last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                    <td>{p.tokenName}</td>
                    <td className="font-mono">{p.tokenSymbol}</td>
                    <td>
                      <span className={
                        p.status === "completed" ? "text-green-500" :
                        p.status === "failed" ? "text-red-500" : "text-app-muted"
                      }>{p.status}</span>
                      {p.errorMessage && (
                        <p className="text-xs text-red-500 mt-1" title={p.errorMessage}>
                          {p.errorMessage.slice(0, 60)}...
                        </p>
                      )}
                    </td>
                    <td>
                      {p.tokenAddress ? (
                        <a target="_blank" rel="noreferrer"
                           className="text-app-accent underline text-xs font-mono"
                           href={`https://sepolia.basescan.org/address/${p.tokenAddress}`}>
                          {p.tokenAddress.slice(0, 10)}...
                        </a>
                      ) : "—"}
                    </td>
                    <td className="text-xs text-app-muted pr-3">
                      {new Date(p.createdAt).toLocaleString("pt-BR")}
                    </td>
                    <td className="pr-3 py-1">
                      {(p.status === "failed" || (p.status === "completed" && !p.vestingFactoryAddress)) && (
                        <button
                          type="button"
                          onClick={() => onResume(p.slug)}
                          disabled={resumingSlug === p.slug}
                          className="bg-amber-500/20 text-amber-600 border border-amber-500/40 rounded px-3 py-1 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-50"
                          title="Continuar deploy de onde parou"
                        >
                          {resumingSlug === p.slug ? "Deployando..." : "↻ Continuar"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
